import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback, useMemo } from 'react';

/**
 * @deprecated AppContext is deprecated. Use useOnboard hook instead for account management.
 * This context is kept for backward compatibility and syncs with useOnboard's localStorage keys.
 */
interface AppContextType {
  generatedAddress: string | null;
  mnemonic: string | null;
  setGeneratedData: (address: string, mnemonic: string) => void;
  clearGeneratedData: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Use the same localStorage keys as useOnboard for consistency
const STORAGE_KEY_MNEMONIC = 'onboard_mnemonic';
const STORAGE_KEY_WALLET_ADDRESS = 'onboard_wallet_address';

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [generatedAddress, setGeneratedAddress] = useState<string | null>(null);
  const [mnemonic, setMnemonic] = useState<string | null>(null);

  // Load from localStorage on init (using useOnboard's keys for consistency)
  useEffect(() => {
    const storedAddress = localStorage.getItem(STORAGE_KEY_WALLET_ADDRESS);
    const storedMnemonic = localStorage.getItem(STORAGE_KEY_MNEMONIC);
    
    // Also check old keys for backward compatibility
    const oldAddress = localStorage.getItem('generatedAddress');
    const oldMnemonic = localStorage.getItem('mnemonic');
    
    if (storedAddress && storedMnemonic) {
      setGeneratedAddress(storedAddress);
      setMnemonic(storedMnemonic);
    } else if (oldAddress && oldMnemonic) {
      // Migrate old keys to new keys
      setGeneratedAddress(oldAddress);
      setMnemonic(oldMnemonic);
      localStorage.setItem(STORAGE_KEY_WALLET_ADDRESS, oldAddress);
      localStorage.setItem(STORAGE_KEY_MNEMONIC, oldMnemonic);
      localStorage.removeItem('generatedAddress');
      localStorage.removeItem('mnemonic');
    }
  }, []);

  // Listen for storage changes to sync with useOnboard
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY_WALLET_ADDRESS) {
        setGeneratedAddress(e.newValue);
      } else if (e.key === STORAGE_KEY_MNEMONIC) {
        setMnemonic(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const setGeneratedData = useCallback((address: string, mnemonic: string) => {
    setGeneratedAddress(address);
    setMnemonic(mnemonic);
    // Persist to localStorage using useOnboard's keys for consistency
    localStorage.setItem(STORAGE_KEY_WALLET_ADDRESS, address);
    localStorage.setItem(STORAGE_KEY_MNEMONIC, mnemonic);
  }, []);

  const clearGeneratedData = useCallback(() => {
    setGeneratedAddress(null);
    setMnemonic(null);
    localStorage.removeItem(STORAGE_KEY_WALLET_ADDRESS);
    localStorage.removeItem(STORAGE_KEY_MNEMONIC);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    generatedAddress,
    mnemonic,
    setGeneratedData,
    clearGeneratedData,
  }), [generatedAddress, mnemonic, setGeneratedData, clearGeneratedData]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

/**
 * @deprecated Use useOnboard hook instead for account management.
 * This hook is kept for backward compatibility.
 */
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppContext must be used within AppProvider');
  return context;
};
